import { Controller } from '@hotwired/stimulus'
import Uploader from '../src/uploader'
import { setFlash } from '../src/helpers'

export default class extends Controller {
  static targets = ['fileField', 'file', 'countValue']
  static values = {
    count: Number,
    max: Number,
    min: Number,
    maxDimension: { type: Number, default: 3840 },
    maxBytes: { type: Number, default: 52428800 }, // 50mb
    whitelist: { type: Array, default: ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'] }
  }

  connect () {
    this.updateCount()
  }

  handleFileClick () {
    if (this.hasMaxValue && this.maxValue > 0 && this.countValue >= this.maxValue) {
      setFlash('alert', `Maximum files allowed: ${this.maxValue}`)
      return false
    }

    this.fileFieldTarget.click()
  }

  async handleNewFiles (event) {
    const files = Array.from(event.target.files)

    if (this.hasMaxValue && this.maxValue > 0 && files.length + this.countValue > this.maxValue) {
      setFlash('alert', `Maximum files allowed: ${this.maxValue}`)
      return false
    }

    const approvedFiles = await Promise.all(files.map(file => this.approveFile(file)))
    const results = approvedFiles.filter(Boolean).map(f => f)

    this.submitFiles(results)
    this.fileFieldTarget.value = null
  }

  async approveFile (file) {
    if (!this.whitelistValue.includes(file.type)) {
      setFlash('alert', `Invalid file type: ${file.name}`)
      return false
    }

    if (file.size > this.maxBytesValue) {
      setFlash('alert', `File size too large: ${file.name}`)
      return false
    }

    if (this.hasMaxDimValue) {
      const largestDimension = await this.getLargestDimension(file)

      if (largestDimension > this.maxDimensionValue) {
        setFlash('alert', `Dimensions are too lage: ${file.name}`)
        return false
      }
    }

    return file
  }

  getLargestDimension (file) {
    return new Promise(resolve => {
      const img = new Image()
      img.src = URL.createObjectURL(file)

      img.onload = () => {
        const largestDimension = Math.max(img.width, img.height)
        URL.revokeObjectURL(img.src)
        resolve(largestDimension)
      }
    })
  }

  createAttachment (event) {
    return this.element.nestedForm.addAssociation(event)
  }

  removeAttachment (event) {
    this.element.nestedForm.removeAssociation(event)
    this.updateCount()
  }

  submitFiles (files) {
    files.forEach(file => {
      const attachment = this.createAttachment()

      const progressHtml = `
        <div class="upload-progress">
          <div class="progress-bar"></div>
        </div>
      `
      attachment.insertAdjacentHTML('beforeend', progressHtml)
      attachment.querySelector('.filename').innerText = file.name

      if (attachment.querySelector('img')) {
        attachment.querySelector('img').src = URL.createObjectURL(file)
      }

      const uploader = new Uploader(file, {
        id: attachment.dataset.tempId,
        onChangeFile: this.handleFileProgress
      })

      uploader.start()
    })
  }

  handleFileProgress = event => {
    const attachment = document.querySelector(`[data-temp-id="${event.id}"]`)

    // The row can be removed mid-upload (user removed it, or the form re-rendered)
    if (!attachment) return

    switch (event.state) {
      case 'uploading':
        attachment.querySelector('.progress-bar').style.width = `${event.progress}%`
        break
      case 'error':
        attachment.remove()
        setFlash('alert', `Error uploading ${event.file.name}.`)
        break
      case 'finished': {
        attachment.querySelector('.upload-progress').remove()

        const hiddenSignedIdField = document.createElement('input')

        hiddenSignedIdField.setAttribute('type', 'hidden')
        hiddenSignedIdField.setAttribute('value', event.signedId)
        hiddenSignedIdField.name = attachment.querySelector("input[name*='_destroy']").name.replace(/_destroy/g, 'file')

        attachment.appendChild(hiddenSignedIdField)

        this.updateCount()
        break
      }
      default:
        break
    }
  }

  get activeFiles() {
    return this.fileTargets.filter(file => !file.classList.contains('destroying'))
  }

  updateCount () {
    this.countValue = this.activeFiles.length
  }

  countValueChanged () {
    if (this.hasCountValueTarget) {
      this.countValueTarget.value = this.countValue
    }
  }
}
